// 머리 소품 모델. 프로토타입 1002–1166 (기본 5종, 라군 6종, 찜질방 4종).
// 움직이는 소품(빙하, 퍼핀, 미니 간헐천, 오로라 구름)은 userData.kind를 달고, items/fx.ts가 매 프레임 움직인다.
import * as THREE from 'three';
import type { HeadItemId } from '@capy/shared';
import { S, lathe, legacyCone, lineGeo, part } from '../engine/geometry';
import { plush, smooth } from '../engine/materials';
import { alphaTex, steamTex } from '../engine/textures';
import { inkMat } from '../capy/model';

function leaf() { const l = part(S(0.09, 16, 8), smooth('#5E9B4F')); l.scale.set(1, 0.25, 0.5); return l; }
function citrus(color: string, r: number, sy: number) {
  const g = new THREE.Group();
  const f = part(S(r, 16, 12), smooth(color)); f.scale.y = sy; f.position.y = r * sy - 0.02; g.add(f);
  const st = part(lineGeo(0.06), smooth('#6B4A2B')); st.scale.set(2, 1, 2); st.position.y = 2 * r * sy; g.add(st);
  const l = leaf(); l.position.set(0.07, 2 * r * sy, 0); l.rotation.z = -0.4; g.add(l);
  return g;
}

// ---- 라군 소품 재질 ----
/** 빙하 소품 속 (라군 밤에 더 빛난다). */
export const GLACIER_MAT = new THREE.MeshStandardMaterial({ color: 0x8FD0F0, roughness: 0.1, metalness: 0, transparent: true, opacity: 0.35, depthWrite: false, emissive: 0x3FA0D8, emissiveIntensity: 0.22, flatShading: true });
const DRIP_MAT = new THREE.MeshStandardMaterial({ color: 0xBFE6F7, roughness: 0.05, transparent: true, opacity: 0.8 });
/** 밤에 오로라 색으로 빛나는 구름 (라군 시간이 emissive를 바꾼다). */
export const AUR_CLOUD_MAT = plush('#F7F9FB', '#FFFFFF', 0.12);
AUR_CLOUD_MAT.emissive = new THREE.Color(0x2BD27A);
AUR_CLOUD_MAT.emissiveIntensity = 0;
const LOPI_MAT = (() => {
  const c = document.createElement('canvas'); c.width = 256; c.height = 64; const x = c.getContext('2d')!;
  x.fillStyle = '#7FD8C4'; x.fillRect(0, 0, 256, 64);
  x.fillStyle = '#F4F2EC'; x.fillRect(0, 0, 256, 6); x.fillRect(0, 58, 256, 6);
  for (let k = 0; k < 8; k++) {
    const cx = k * 32 + 16;
    x.fillStyle = '#F4F2EC'; [[0, -14], [0, 14], [-14, 0], [14, 0], [-9, -9], [9, 9], [-9, 9], [9, -9]].forEach(([dx, dy]) => x.fillRect(cx + dx! - 3, 32 + dy! - 3, 6, 6));
    x.fillStyle = '#2E7D74'; x.fillRect(cx - 4, 28, 8, 8);
    x.fillStyle = '#F4F2EC'; x.fillRect(k * 32 - 2, 22, 4, 4); x.fillRect(k * 32 - 2, 38, 4, 4);
  }
  const t = new THREE.CanvasTexture(c); t.wrapS = THREE.RepeatWrapping;
  return new THREE.MeshStandardMaterial({ map: t, roughness: 1 });
})();
// 빙하 반짝임: 둥근 빛 + 네 갈래 별 (투명도만 쓰고 색은 흰색으로 고정, textures.ts alphaTex)
const glintTex = alphaTex(64, 64, (x) => {
  const gr = x.createRadialGradient(32, 32, 0, 32, 32, 10); gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(1, 'rgba(255,255,255,0)'); x.fillStyle = gr; x.fillRect(0, 0, 64, 64);
  x.fillStyle = 'rgba(255,255,255,0.9)'; x.beginPath(); x.moveTo(32, 0); x.lineTo(35, 29); x.lineTo(64, 32); x.lineTo(35, 35); x.lineTo(32, 64); x.lineTo(29, 35); x.lineTo(0, 32); x.lineTo(29, 29); x.closePath(); x.fill();
});
/** 빙하 소품 겉 얼음 (라군 밤에 더 빛난다). */
export const ICE_MAT = new THREE.MeshStandardMaterial({ color: 0xCDEFFC, roughness: 0.04, metalness: 0.15, transparent: true, opacity: 0.62, emissive: 0x5FB8E8, emissiveIntensity: 0.22, flatShading: true, side: THREE.DoubleSide, depthWrite: false });
const ICE_EDGE = new THREE.LineBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.55 });
/** 미니 간헐천 화산석: 검붉은 현무암 색, 각진 면 (프로토타입은 매끈한 짙은 회색). */
const VOLC_ROCK = plush('#3B2D28', '#7A5544', 0.07);
VOLC_ROCK.flatShading = true;
const itemSprite = () => new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 }));

const MAKE: Record<HeadItemId, () => THREE.Object3D> = {
  orange: () => citrus('#F2902A', 0.2, 0.88),
  yuzu: () => citrus('#EFC93A', 0.18, 0.85),
  duckling: () => {
    const g = new THREE.Group(), y = smooth('#F5D04A');
    const b = part(S(0.16, 20, 14), y); b.scale.set(1, 0.8, 1.2); b.position.y = 0.11; g.add(b);
    const h = part(S(0.1, 18, 12), y); h.position.set(0, 0.28, 0.1); g.add(h);
    const bk = part(S(0.045, 12, 8), smooth('#E98A2B')); bk.scale.set(1.2, 0.5, 1); bk.position.set(0, 0.27, 0.2); g.add(bk);
    [1, -1].forEach((s) => { const e = part(S(0.016, 8, 6), inkMat); e.position.set(s * 0.055, 0.31, 0.18); g.add(e); });
    return g;
  },
  frog: () => {
    const g = new THREE.Group(), gr = smooth('#79B05A');
    const b = part(S(0.16, 20, 14), gr); b.scale.set(1.1, 0.62, 1); b.position.y = 0.09; g.add(b);
    [1, -1].forEach((s) => {
      const e = part(S(0.055, 14, 10), gr); e.position.set(s * 0.08, 0.18, 0.07); g.add(e);
      const p = part(S(0.024, 10, 8), inkMat); p.position.set(s * 0.08, 0.2, 0.12); g.add(p);
    });
    return g;
  },
  towel: () => {
    const g = new THREE.Group(), w = smooth('#F7F5EF'), st = smooth('#3F5A8C');
    const a1 = part(new THREE.BoxGeometry(0.56, 0.07, 0.3), w); a1.position.y = 0.035; g.add(a1);
    const a2 = part(new THREE.BoxGeometry(0.5, 0.07, 0.28), w); a2.position.set(0.02, 0.1, 0); a2.rotation.y = 0.05; g.add(a2);
    const fold = part(new THREE.CylinderGeometry(0.068, 0.068, 0.3, 12), w); fold.rotation.x = Math.PI / 2; fold.position.set(0.27, 0.068, 0); g.add(fold);
    [-0.21, -0.17].forEach((x) => { const sp = part(new THREE.BoxGeometry(0.02, 0.142, 0.305), st); sp.position.set(x, 0.068, 0); g.add(sp); });
    g.rotation.y = 0.3;
    return g;
  },
  glacier: () => {
    const g = new THREE.Group();
    const hsh = (x: number, z: number, k: number) => { const v = Math.sin(Math.round(x * 500) * 12.9898 + Math.round(z * 500) * 78.233 + k * 37.7) * 43758.5453; return v - Math.floor(v); };
    const shape = (geo0: THREE.BufferGeometry, lean: number, squash: number) => {
      const p = geo0.attributes.position!;
      for (let i2 = 0; i2 < p.count; i2++) {
        const x = p.getX(i2), y = p.getY(i2), z = p.getZ(i2), r = Math.hypot(x, z);
        const jr = r > 0.001 ? 1 + 0.22 * (hsh(x, z, 1) - 0.5) : 1, jy = r > 0.001 ? 0.04 * (hsh(x, z, 2) - 0.5) : 0;
        p.setXYZ(i2, x * jr + Math.pow(Math.max(0, y) / 0.52, 1.6) * lean, y + jy, z * jr * squash);
      }
      const ni = geo0.toNonIndexed(); ni.computeVertexNormals(); return ni;
    };
    const cg = legacyCone(0.28, 0.52, 7, 4); cg.translate(0, 0.26, 0);
    const geo = shape(cg, 0.07, 0.74);
    const ice = part(geo, ICE_MAT); ice.castShadow = false; ice.renderOrder = 2; g.add(ice);
    const core = part(geo, GLACIER_MAT); core.scale.set(0.55, 0.75, 0.55); core.position.y = 0.01; g.add(core);
    ([[0.2, 0.06, 0.6, 0.11, 0.05], [-0.19, 0.03, -0.5, 0.13, 0.055], [0.05, -0.17, 0.45, 0.1, 0.045], [-0.05, 0.16, -0.4, 0.09, 0.04]] as const).forEach(([x, z, tilt, h, r]) => {
      const sg = legacyCone(r, h, 4, 1); sg.translate(0, h / 2, 0);
      const sh = part(sg.toNonIndexed(), ICE_MAT); sh.geometry.computeVertexNormals(); sh.castShadow = false;
      sh.position.set(x, 0, z); sh.rotation.set(Math.atan2(z, 1) * 0.8, tilt * 2, -x * 2.2); g.add(sh);
      const ed = new THREE.LineSegments(new THREE.EdgesGeometry(sg, 20), ICE_EDGE); ed.position.copy(sh.position); ed.rotation.copy(sh.rotation); g.add(ed);
    });
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 18), ICE_EDGE); g.add(edges);
    const glints: THREE.Sprite[] = [];
    for (let k = 0; k < 5; k++) {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: glintTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 }));
      const y = 0.06 + k * 0.085, a = k * 2.3;
      sp.position.set(Math.cos(a) * (0.27 * (1 - y / 0.52) + 0.02) + Math.pow(y / 0.52, 1.6) * 0.07, y, Math.sin(a) * (0.27 * (1 - y / 0.52) + 0.02) * 0.74);
      g.add(sp); glints.push(sp);
    }
    const drip = part(S(0.022, 8, 6), DRIP_MAT); drip.scale.set(0.8, 1.3, 0.8); g.add(drip);
    const steam = [itemSprite(), itemSprite()]; steam.forEach((sp) => g.add(sp));
    g.scale.setScalar(1.2); g.userData = { kind: 'glacier', drip, steam, glints, seed: Math.random() * 10 };
    return g;
  },
  puffin: () => {
    const g = new THREE.Group(), blk = smooth('#24272C'), wht = smooth('#F4F2EC'), org = smooth('#EF6F2A'), gry = smooth('#C9C6BE');
    const b = part(S(0.15, 20, 14), blk); b.scale.set(1, 1.15, 1.05); b.position.y = 0.16; g.add(b);
    const belly = part(S(0.125, 18, 12), wht); belly.scale.set(0.88, 1.05, 0.62); belly.position.set(0, 0.14, 0.075); g.add(belly);
    const h = part(S(0.1, 18, 12), blk); h.position.set(0, 0.35, 0.03); g.add(h);
    const face = part(S(0.085, 16, 10), wht); face.scale.set(1.05, 0.85, 0.55); face.position.set(0, 0.35, 0.08); g.add(face);
    const base = part(S(0.034, 10, 8), gry); base.scale.set(0.75, 1.05, 0.5); base.position.set(0, 0.33, 0.125); g.add(base);
    const beak = part(S(0.05, 12, 8), org); beak.scale.set(0.5, 0.95, 1.1); beak.position.set(0, 0.325, 0.16); g.add(beak);
    [1, -1].forEach((sd) => {
      const e = part(S(0.015, 8, 6), inkMat); e.position.set(sd * 0.042, 0.365, 0.115); g.add(e);
      const f = part(S(0.04, 10, 8), org); f.scale.set(1, 0.35, 1.4); f.position.set(sd * 0.055, 0.02, 0.06); g.add(f);
    });
    const wings = [1, -1].map((sd) => {
      const pv = new THREE.Group(); pv.position.set(sd * 0.14, 0.24, 0); g.add(pv);
      const w = part(S(0.07, 12, 8), blk); w.scale.set(0.4, 1.45, 1); w.position.y = -0.07; pv.add(w);
      return pv;
    });
    g.scale.setScalar(1.3); g.userData = { kind: 'puffin', wings, seed: Math.random() * 10 };
    return g;
  },
  minigeyser: () => {
    const g = new THREE.Group();
    // 울퉁불퉁한 화산 원뿔: 프로토타입보다 분할을 늘리고 굴곡을 여러 겹 더했다. 이음매가 갈라지지 않게 각도의 주기는 정수.
    const prof = [[0.3, 0], [0.29, 0.025], [0.27, 0.05], [0.235, 0.09], [0.2, 0.13], [0.165, 0.17], [0.13, 0.21], [0.095, 0.25], [0.085, 0.245], [0.07, 0.215], [0.0, 0.21]].map(([r, y]) => new THREE.Vector2(r, y));
    const geo = new THREE.LatheGeometry(prof, 30), pp = geo.attributes.position!;
    for (let k = 0; k < pp.count; k++) {
      const x = pp.getX(k), z = pp.getZ(k), y = pp.getY(k), ang = Math.atan2(z, x);
      const n = 1 + 0.08 * Math.sin(ang * 5 + y * 20) + 0.06 * Math.sin(ang * 11 - y * 37) + 0.04 * Math.sin(ang * 17 + y * 61);
      pp.setXYZ(k, x * n, y + (y > 0.005 && y < 0.2 ? 0.012 * Math.sin(ang * 7 + y * 45) : 0), z * n);
    }
    geo.computeVertexNormals();
    const cone = part(geo, VOLC_ROCK); g.add(cone);
    // 밑동에 굴러다니는 작은 화산석
    for (let k = 0; k < 7; k++) { const ang = k / 7 * Math.PI * 2 + 0.4, rock = part(new THREE.IcosahedronGeometry(0.035 + 0.015 * (k % 3), 0), VOLC_ROCK); rock.position.set(Math.cos(ang) * 0.29, 0.015, Math.sin(ang) * 0.29); rock.rotation.set(k, k * 2, 0); g.add(rock); }
    const snow = part(new THREE.TorusGeometry(0.1, 0.018, 6, 18), smooth('#EEF2F5')); snow.rotation.x = Math.PI / 2; snow.position.y = 0.245; snow.scale.set(1, 1, 0.6); g.add(snow);
    const pool = part(new THREE.CircleGeometry(0.075, 18), new THREE.MeshStandardMaterial({ color: 0x5FCAD6, roughness: 0.1, emissive: 0x2BB5B8, emissiveIntensity: 0.4 })); pool.rotation.x = -Math.PI / 2; pool.position.y = 0.222; g.add(pool);
    const dome = part(new THREE.SphereGeometry(0.06, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0x8EDCE4, roughness: 0.1, transparent: true, opacity: 0.85, emissive: 0x2BB5B8, emissiveIntensity: 0.3 })); dome.position.y = 0.222; dome.scale.setScalar(0.01); g.add(dome);
    const jg = new THREE.CylinderGeometry(0.018, 0.045, 1, 12, 1, true); jg.translate(0, 0.5, 0);
    const jetMat = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0, depthWrite: false });
    const jet = new THREE.Mesh(jg, jetMat); jet.position.y = 0.22; g.add(jet);
    const sheath = new THREE.Mesh(jg, new THREE.MeshBasicMaterial({ color: 0xDDF3FA, transparent: true, opacity: 0, depthWrite: false })); sheath.position.y = 0.22; g.add(sheath);
    const dropMat = new THREE.MeshBasicMaterial({ color: 0xF2FBFF, transparent: true, opacity: 0, depthWrite: false });
    const drops: THREE.Mesh[] = [];
    for (let k = 0; k < 14; k++) { const d = new THREE.Mesh(S(0.016, 6, 5), dropMat); g.add(d); drops.push(d); }
    const steam = [itemSprite(), itemSprite(), itemSprite()]; steam.forEach((sp) => g.add(sp));
    g.scale.setScalar(1.25); g.userData = { kind: 'minigeyser', jet, sheath, dome, drops, dropMat, steam, seed: Math.random() * 10 };
    return g;
  },
  auroracloud: () => {
    const g = new THREE.Group();
    ([[0, 0.15, 0, 0.13], [0.14, 0.12, 0.03, 0.1], [-0.14, 0.12, -0.01, 0.105], [0.04, 0.26, 0.02, 0.1], [-0.07, 0.24, -0.03, 0.085], [0.12, 0.21, -0.04, 0.075], [-0.04, 0.09, 0.11, 0.09], [0.08, 0.09, -0.1, 0.085], [0.2, 0.08, -0.02, 0.07], [-0.21, 0.08, 0.02, 0.07], [0.1, 0.1, 0.11, 0.075], [-0.1, 0.1, -0.11, 0.075], [0, 0.32, 0, 0.06]] as const)
      .forEach(([x, y, z, r]) => { const m = part(S(r, 22, 16), AUR_CLOUD_MAT); m.position.set(x, y, z); m.scale.set(1, 0.9, 1); g.add(m); });
    g.scale.setScalar(1.12);
    g.userData = { kind: 'auroracloud', seed: Math.random() * 10 };
    return g;
  },
  lopapeysa: () => {
    const g = new THREE.Group(), mint = smooth('#7FD8C4'), cuffC = smooth('#6CCDB7'), white = new THREE.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 1, emissive: 0xFFFFFF, emissiveIntensity: 0.22 });
    const prof: THREE.Vector2[] = [];
    for (let k = 0; k <= 20; k++) { const u = k / 20, y = u * 0.68, r = 0.43 * Math.cos(Math.pow(u, 1.9) * Math.PI / 2) + 0.015 * (1 - u); prof.push(new THREE.Vector2(Math.max(0.005, r), y)); }
    const cap = part(new THREE.LatheGeometry(prof, 32), mint); cap.scale.set(0.96, 1, 1.01); cap.position.y = -0.05; g.add(cap);
    const yoke = part(new THREE.CylinderGeometry(0.432, 0.452, 0.17, 32, 1, true), LOPI_MAT); yoke.position.y = 0.06; yoke.scale.z = 1.05; g.add(yoke);
    const cuff = part(new THREE.TorusGeometry(0.44, 0.05, 10, 32), cuffC); cuff.rotation.x = Math.PI / 2; cuff.position.y = -0.04; cuff.scale.set(1, 1.05, 1); g.add(cuff);
    const pom = new THREE.Group(); pom.position.y = 0.68; g.add(pom);
    const pg = new THREE.SphereGeometry(0.135, 28, 20), pp = pg.attributes.position!;
    for (let k = 0; k < pp.count; k++) { const x = pp.getX(k), y = pp.getY(k), z = pp.getZ(k), n = 1 + 0.05 * Math.sin(x * 70 + y * 35) * Math.sin(z * 65 - y * 25) + 0.025 * Math.sin(x * 130 + z * 120); pp.setXYZ(k, x * n, y * n, z * n); }
    pg.computeVertexNormals(); pom.add(part(pg, white));
    return g;
  },
  hotdog: () => {
    const g = new THREE.Group();
    const bun = part(S(0.08, 18, 12), smooth('#DFA35C')); bun.scale.set(2.5, 0.75, 1.1); bun.position.y = 0.06; g.add(bun);
    const dog = part(S(0.055, 16, 10), smooth('#C4553E')); dog.scale.set(3.5, 0.9, 0.9); dog.position.y = 0.105; g.add(dog);
    const zig = (yy: number, off: number, color: string) => {
      const pts: THREE.Vector3[] = [];
      for (let k = 0; k <= 14; k++) { const xx = -0.16 + k / 14 * 0.32; pts.push(new THREE.Vector3(xx, yy, (k % 2 ? 1 : -1) * 0.026 + off)); }
      g.add(part(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 60, 0.009, 6), smooth(color)));
    };
    zig(0.152, 0, '#B7832E'); zig(0.158, 0.008, '#F1E7C2');
    for (let k = 0; k < 9; k++) { const o = part(new THREE.BoxGeometry(0.022, 0.012, 0.016), smooth('#C08A45')); o.position.set(-0.13 + k * 0.032, 0.166, (k % 3 - 1) * 0.018); o.rotation.y = k; g.add(o); }
    g.rotation.y = 0.25; g.scale.setScalar(1.35);
    return g;
  },
  yangmeori: () => {
    const g = new THREE.Group(), tw = plush('#F7C6D0', '#FFFFFF', 0.25), tw2 = plush('#FBE3E8', '#FFFFFF', 0.2);
    const cap = part(new THREE.SphereGeometry(0.42, 24, 14, 0, Math.PI * 2, 0, Math.PI / 2), tw); cap.scale.set(1.02, 0.55, 1.05); cap.position.y = -0.1; g.add(cap);
    const band = part(new THREE.TorusGeometry(0.42, 0.075, 10, 28), tw2); band.rotation.x = Math.PI / 2 - 0.32; band.position.set(0, -0.14, 0.02); band.scale.set(1.03, 1.08, 1); g.add(band);
    const brow = part(new THREE.CylinderGeometry(0.085, 0.085, 0.62, 16), tw); brow.rotation.z = Math.PI / 2; brow.position.set(0, -0.24, 0.36); brow.scale.set(1, 1, 0.85); g.add(brow);
    const brow2 = part(new THREE.CylinderGeometry(0.05, 0.05, 0.56, 12), tw2); brow2.rotation.z = Math.PI / 2; brow2.position.set(0, -0.17, 0.4); g.add(brow2);
    [1, -1].forEach((sd) => {
      const coil = new THREE.Group(); coil.position.set(sd * 0.4, -0.02, 0); coil.rotation.y = sd * Math.PI / 2; g.add(coil);
      ([[0.15, 0.06], [0.09, 0.055], [0.035, 0.04]] as const).forEach(([r, tube], i) => { const t2 = part(new THREE.TorusGeometry(r, tube, 10, 22), i % 2 ? tw2 : tw); t2.position.z = i * 0.045; coil.add(t2); });
    });
    return g;
  },
  stoneegg: () => {
    const g = new THREE.Group(), e = part(S(0.13, 18, 14), smooth('#B07A4E')); e.scale.set(1, 1.28, 1); e.position.y = 0.16; g.add(e);
    const sh = part(S(0.03, 8, 6), new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.35 })); sh.position.set(-0.05, 0.26, 0.09); g.add(sh);
    return g;
  },
  bananamilk: () => {
    const g = new THREE.Group();
    g.add(part(lathe([[0, 0], [0.09, 0], [0.1, 0.02], [0.1, 0.22], [0.075, 0.27], [0.055, 0.29], [0.055, 0.32], [0, 0.32]], 22), new THREE.MeshStandardMaterial({ color: 0xEAF4F6, roughness: 0.08, transparent: true, opacity: 0.4 })));
    const milk = part(lathe([[0, 0], [0.093, 0], [0.093, 0.22], [0.07, 0.26], [0, 0.26]], 22), smooth('#F6D86A')); milk.position.y = 0.005; g.add(milk);
    const label = part(new THREE.CylinderGeometry(0.102, 0.102, 0.08, 22, 1, true), smooth('#FFFFFF')); label.position.y = 0.12; g.add(label);
    const cap = part(new THREE.CylinderGeometry(0.058, 0.058, 0.03, 16), smooth('#F2C94C')); cap.position.y = 0.33; g.add(cap);
    const straw = part(new THREE.CylinderGeometry(0.01, 0.01, 0.26, 8), smooth('#FFFFFF')); straw.position.set(0.02, 0.43, 0); straw.rotation.z = -0.25; g.add(straw);
    g.scale.setScalar(1.1);
    return g;
  },
  bagaji: () => {
    const g = new THREE.Group();
    const pts = [[0, 0], [0.16, 0.02], [0.27, 0.12], [0.29, 0.24], [0.25, 0.35], [0.13, 0.43], [0.085, 0.5], [0.1, 0.58], [0.095, 0.66], [0.02, 0.71]].map(([x, y]) => new THREE.Vector2(x, y));
    const m = part(new THREE.LatheGeometry(pts, 28, 0, Math.PI), new THREE.MeshStandardMaterial({ color: 0xD8B26A, roughness: 0.7, side: THREE.DoubleSide }));
    // 머리에 폭 씌우도록 프로토타입(y 0.07)보다 내림: 평평한 테두리가 둥근 머리 위에서 떠 보였다
    m.rotation.set(Math.PI / 2, 0, Math.PI / 2, 'ZYX'); m.position.set(0, -0.03, -0.24); g.add(m);
    g.rotation.y = 0.5;
    return g;
  },
};

export function makeHeadItem(key: HeadItemId): THREE.Object3D {
  return MAKE[key]();
}
